// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Camera, CameraResultType, type ImageOptions } from "@capacitor/camera";
import { isDefined } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../analytics";
import { base64ToBlob, getFileTypeFromBlob, isAcceptableImageUploadContentType } from "../files";
import { CameraPermissionDeniedError } from "./CameraPermissionDeniedError";
import { checkAndRequestPhotoPermissions } from "./checkAndRequestPhotoPermissions";
import { DEFAULT_CAMERA_IMAGE_QUALITY } from "./constants";
import { type AllowedCameraSourceType, type UploadFileData } from "./types";

interface TakePhotoProps {
  source: AllowedCameraSourceType;
  onSuccess: (photo: UploadFileData) => void;
  onError: (error: Error | CameraPermissionDeniedError) => void;
  photoOptions?: Pick<ImageOptions, "height" | "width" | "quality">;
}

export function useTakePhoto(props: TakePhotoProps): () => Promise<void> {
  const { source, onSuccess, onError, photoOptions } = props;

  return async () => {
    try {
      await checkAndRequestPhotoPermissions(source);

      const { dataUrl, path } = await Camera.getPhoto({
        source,
        resultType: CameraResultType.DataUrl,
        ...photoOptions,
        quality: photoOptions?.quality ?? DEFAULT_CAMERA_IMAGE_QUALITY,
      });

      if (!isDefined(dataUrl)) {
        throw new Error("Invalid Camera Result");
      }

      const dataUrlBase64 = dataUrl;
      const blob = await base64ToBlob(dataUrlBase64);
      const { extension, contentType } = await getFileTypeFromBlob(blob);

      if (!isAcceptableImageUploadContentType(contentType)) {
        throw new Error(`Image with unsupported content type picked: ${contentType}`);
      }

      const filename = path?.split("/").pop();
      onSuccess({
        dataUrlBase64,
        blob,
        extension,
        contentType,
        name: filename,
      });
    } catch (error) {
      const errorLogEvent =
        error instanceof CameraPermissionDeniedError
          ? APP_V2_APP_EVENTS.DOCUMENT_UPLOAD_CAMERA_TAKE_PHOTO_PERMISSION_DENIED
          : APP_V2_APP_EVENTS.DOCUMENT_UPLOAD_CAMERA_TAKE_PHOTO_FAILURE;
      logError(errorLogEvent, { error });
      onError(error as Error | CameraPermissionDeniedError);
    }
  };
}
